import { useCallback, useEffect, useMemo, useState } from "react";
import { StepStatus, type StepStatusValue } from "@components/StepperTabs/StepperTabs.types";
import type { FieldErrors, FieldPath, FieldValues } from "react-hook-form";
import type { ZodTypeAny } from "zod";

type RHFFormLike<T extends FieldValues> = {
  getValues: () => T;
  formState: { errors: FieldErrors<T> };
};

type StepField<T extends FieldValues> = FieldPath<T> | string;
type GetStepFields<T extends FieldValues> = Record<number, StepField<T>[]>;

type Params<T extends FieldValues> = {
  schema: ZodTypeAny;
  form: RHFFormLike<T>;
  stepFields: GetStepFields<T>;
  touchedSteps: Record<number, boolean>;
  currentStepIndex: number;
  // Optional RHF errors subscription (e.g. from useFormState). Defaults to form.formState.errors
  errors?: FieldErrors<T>;
  // Optional server/API error field names. When set, those fields count as step ERRORS
  serverErrorFields?: ReadonlySet<string>;
  // When true, treat every step as touched for COMPLETE/INCOMPLETE (after submit attempt).
  // Defaults to false so other steppers keep prior behavior.
  validateAllSteps?: boolean;
};

const getByPath = (value: unknown, path: string): unknown =>
  path
    .split(".")
    .reduce<unknown>(
      (acc, key) =>
        acc && typeof acc === "object" ? (acc as Record<string, unknown>)[key] : undefined,
      value,
    );

const pathsMatch = (stepField: string, issueField: string): boolean =>
  stepField === issueField ||
  issueField.startsWith(`${stepField}.`) ||
  stepField.startsWith(`${issueField}.`);

function hasFormErrorFor<T extends FieldValues>(
  errors: FieldErrors<T>,
  key: StepField<T>,
): boolean {
  return Boolean(getByPath(errors, String(key)));
}

export function useStepStatuses<T extends FieldValues>({
  schema,
  form,
  stepFields,
  touchedSteps,
  currentStepIndex,
  errors: errorsOverride,
  serverErrorFields,
  validateAllSteps = false,
}: Params<T>) {
  const errors = errorsOverride ?? form.formState.errors;
  const [validationIssueFields, setValidationIssueFields] = useState<Set<string>>(new Set());
  const values = form.getValues();

  useEffect(() => {
    let active = true;

    void schema.safeParseAsync(values).then((result) => {
      if (!active) return;
      if (result.success) {
        setValidationIssueFields((prev) => (prev.size === 0 ? prev : new Set()));
        return;
      }

      const next = new Set(
        result.error.issues
          .map((issue) => issue.path.join("."))
          .filter((path): path is string => typeof path === "string"),
      );

      setValidationIssueFields((prev) => {
        if (prev.size === next.size && [...prev].every((field) => next.has(field))) return prev;
        return next;
      });
    });

    return () => {
      active = false;
    };
  }, [schema, values, errors, touchedSteps]);

  const stepStatuses = useMemo(() => {
    const mapped: Record<number, StepStatusValue> = {};

    Object.keys(stepFields).forEach((key) => {
      const index = Number(key);
      const fields = stepFields[index] ?? [];

      if (!fields.length) {
        mapped[index] = StepStatus.INCOMPLETE;
        return;
      }

      const hasErrors = fields.some(
        (field) => hasFormErrorFor(errors, field) || Boolean(serverErrorFields?.has(String(field))),
      );
      const hasValidationIssues = fields.some((field) =>
        [...validationIssueFields].some((issueField) => pathsMatch(String(field), issueField)),
      );
      const isStepTouched = validateAllSteps || Boolean(touchedSteps[index]);

      if (hasErrors) {
        mapped[index] = StepStatus.ERRORS;
        return;
      }

      if (isStepTouched && hasValidationIssues) {
        mapped[index] = StepStatus.INCOMPLETE;
        return;
      }

      mapped[index] = isStepTouched ? StepStatus.COMPLETE : StepStatus.INCOMPLETE;
    });

    return mapped;
  }, [
    errors,
    serverErrorFields,
    stepFields,
    touchedSteps,
    validateAllSteps,
    validationIssueFields,
  ]);

  const statusFor = useCallback(
    (index: number): StepStatusValue =>
      currentStepIndex === index
        ? StepStatus.CURRENT
        : (stepStatuses[index] ?? StepStatus.INCOMPLETE),
    [currentStepIndex, stepStatuses],
  );

  return { statusFor };
}
